import type { Content, Session } from '@app-types/domain';
import { toLocalDateIso } from '@core/dates';
import { useStore } from '@ui/lib/useStore';

function dateKey(d: Date): string {
  return toLocalDateIso(d);
}

function dowLabel(dow: number): string {
  return ['DOM', 'SEG', 'TER', 'QUA', 'QUI', 'SEX', 'SÁB'][dow];
}

function buildHeatmap(sessions: Session[], days: number) {
  const grid: { date: Date; minutes: number }[] = [];
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  for (let i = days - 1; i >= 0; i--) {
    const d = new Date(today);
    d.setDate(today.getDate() - i);
    const key = dateKey(d);
    const minutes = sessions
      .filter(
        (s) => s.status === 'completed' && s.completed_at && toLocalDateIso(s.completed_at) === key,
      )
      .reduce((s, x) => s + (x.actual_duration_min || 0), 0);
    grid.push({ date: d, minutes });
  }
  return grid;
}

function intensity(min: number, max: number): number {
  if (max === 0) return 0;
  const r = min / max;
  if (r === 0) return 0;
  if (r < 0.25) return 1;
  if (r < 0.5) return 2;
  if (r < 0.75) return 3;
  return 4;
}

const INTENSITY_COLORS = [
  'rgba(0, 8, 24, 0.4)',
  'rgba(0, 240, 255, 0.15)',
  'rgba(0, 240, 255, 0.35)',
  'rgba(0, 240, 255, 0.6)',
  'rgba(0, 240, 255, 0.9)',
];

function buildHourDowGrid(sessions: Session[]): number[][] {
  const grid: number[][] = Array.from({ length: 7 }, () => Array(24).fill(0));
  for (const s of sessions) {
    if (s.status !== 'completed' || !s.started_at) continue;
    const d = new Date(s.started_at);
    grid[d.getDay()][d.getHours()] += s.actual_duration_min || 0;
  }
  return grid;
}

/** ritmo (h/semana) para as últimas 4 semanas */
function ritmoUltimas4Semanas(
  sessions: Session[],
): { week: number; hPerWeek: number; label: string }[] {
  const completed = sessions.filter((s) => s.status === 'completed' && s.completed_at);
  const weeks: { week: number; hPerWeek: number; label: string }[] = [];
  const today = new Date();
  for (let weekAgo = 3; weekAgo >= 0; weekAgo--) {
    const weekStart = new Date(today);
    weekStart.setDate(today.getDate() - weekAgo * 7);
    const weekEnd = new Date(weekStart);
    weekEnd.setDate(weekStart.getDate() + 6);
    const h = completed
      .filter((s) => {
        const d = new Date(s.completed_at as string);
        return d >= weekStart && d <= weekEnd;
      })
      .reduce((sum, s) => sum + (s.actual_duration_min || 0) / 60, 0);
    weeks.push({
      week: weekAgo,
      hPerWeek: h,
      label: `S-${weekAgo}`,
    });
  }
  return weeks;
}

/** horas concluídas por meta */
function progressaoPorMeta(
  sessions: Session[],
  goals: { id: string; title: string; target_hours?: number }[],
): { goalId: string; title: string; horasDone: number; horasTarget: number; pct: number }[] {
  return goals
    .filter((g) => g.target_hours && g.target_hours > 0)
    .map((g) => {
      const completed = sessions.filter((s) => s.goal_id === g.id && s.status === 'completed');
      const horasDone = completed.reduce((sum, s) => sum + (s.actual_duration_min || 0) / 60, 0);
      const horasTarget = g.target_hours as number;
      return {
        goalId: g.id,
        title: g.title,
        horasDone,
        horasTarget,
        pct: Math.min(100, (horasDone / horasTarget) * 100),
      };
    })
    .sort((a, b) => b.pct - a.pct);
}

/** horas por tipo de conteúdo */
function distribuicaoPorTipo(
  sessions: Session[],
  contents: Content[],
): { type: string; horas: number; sessoes: number }[] {
  const byType: Record<string, { horas: number; sessoes: number }> = {};
  for (const s of sessions) {
    if (s.status !== 'completed') continue;
    const c = contents.find((x) => x.id === s.content_id);
    if (!c) continue;
    if (!byType[c.type]) byType[c.type] = { horas: 0, sessoes: 0 };
    byType[c.type].horas += (s.actual_duration_min || 0) / 60;
    byType[c.type].sessoes += 1;
  }
  return Object.entries(byType)
    .map(([type, { horas, sessoes }]) => ({ type, horas, sessoes }))
    .sort((a, b) => b.horas - a.horas);
}

/** média de focus_score (1-5 → 0-100) */
function focusMedio(sessions: Session[]): number | null {
  const withFocus = sessions.filter((s) => s.status === 'completed' && s.focus_score);
  if (withFocus.length === 0) return null;
  const avg = withFocus.reduce((sum, s) => sum + (s.focus_score ?? 3), 0) / withFocus.length;
  return Math.round(((avg - 1) / 4) * 100);
}

/** cards SM-2 que atingiram 3+ reps (dominadas) */
function cardsDominadas(contents: Content[]): { dominadas: number; vencidas: number } {
  const reviewed = contents.filter((c) => c.review);
  const dominadas = reviewed.filter((c) => (c.review?.reps ?? 0) >= 3).length;
  const today = toLocalDateIso(new Date());
  const vencidas = reviewed.filter((c) => (c.review?.due_date ?? '') < today).length;
  return { dominadas, vencidas };
}

/** observações automáticas sobre o padrão de estudo */
function gerarInsights(
  sessions: Session[],
  byDow: number[],
  ritmoSemanas: { hPerWeek: number }[],
): string[] {
  const insights: string[] = [];
  const completed = sessions.filter((s) => s.status === 'completed');
  if (completed.length < 3) {
    insights.push('📊 Comece com 3+ sessões para insights aparecerem');
    return insights;
  }
  const maxDowIdx = byDow.indexOf(Math.max(...byDow));
  if (byDow[maxDowIdx] > 0) {
    insights.push(`📈 Você estuda mais na ${dowLabel(maxDowIdx).toLowerCase()}.`);
  }
  const recent = ritmoSemanas[ritmoSemanas.length - 1]?.hPerWeek ?? 0;
  const previous = ritmoSemanas[ritmoSemanas.length - 2]?.hPerWeek ?? 0;
  if (previous > 0 && recent > previous) {
    const pct = Math.round(((recent - previous) / previous) * 100);
    insights.push(`✨ Ritmo +${pct}% esta semana — ótimo!`);
  } else if (previous > 0 && recent < previous) {
    const pct = Math.round(((previous - recent) / previous) * 100);
    insights.push(`⚠️ Ritmo −${pct}% esta semana — saudade?`);
  }
  const withData = byDow.filter((h) => h > 0).length;
  insights.push(`🎯 Você estuda ${withData}/7 dias da semana.`);
  return insights;
}

export function AnalyticsScreen() {
  const sessions = useStore((s) => s.sessions);
  const contents = useStore((s) => s.contents);
  const goals = useStore((s) => s.goals);

  const completed = sessions.filter((s) => s.status === 'completed');
  const totalHours = completed.reduce((s, x) => s + (x.actual_duration_min || 0) / 60, 0);
  const totalSessions = completed.length;
  const avgSessionMin =
    totalSessions > 0
      ? completed.reduce((s, x) => s + (x.actual_duration_min || 0), 0) / totalSessions
      : 0;

  // Ritmo últimas 4 semanas
  const ritmoSemanas = ritmoUltimas4Semanas(sessions);
  const ritmoAtual = ritmoSemanas[ritmoSemanas.length - 1]?.hPerWeek ?? 0;

  // By day of week
  const byDow = [0, 0, 0, 0, 0, 0, 0];
  completed.forEach((s) => {
    if (!s.completed_at) return;
    const d = new Date(s.completed_at);
    byDow[d.getDay()] += (s.actual_duration_min || 0) / 60;
  });
  const maxDow = Math.max(1, ...byDow);

  // Cumprimento últimas 4 semanas
  const now = new Date();
  const fourWeeksAgo = new Date(now);
  fourWeeksAgo.setDate(now.getDate() - 28);
  const recent = sessions.filter((s) => new Date(s.planned_at) >= fourWeeksAgo);
  const recentDone = recent.filter((s) => s.status === 'completed').length;
  const cumprimento = recent.length > 0 ? Math.round((recentDone / recent.length) * 100) : 0;

  // Hora do dia × dia da semana
  const hourGrid = buildHourDowGrid(sessions);
  const maxCell = Math.max(1, ...hourGrid.flat());
  const activeHours = Array.from({ length: 24 }, (_, h) => h).filter((h) =>
    hourGrid.some((row) => row[h] > 0),
  );
  const hFrom = activeHours.length > 0 ? Math.max(0, Math.min(...activeHours) - 1) : 8;
  const hTo = activeHours.length > 0 ? Math.min(23, Math.max(...activeHours) + 1) : 22;
  const hourRange = Array.from({ length: hTo - hFrom + 1 }, (_, i) => hFrom + i);
  const byHourTotal = Array.from({ length: 24 }, (_, h) =>
    hourGrid.reduce((sum, row) => sum + row[h], 0),
  );
  const peakHour = byHourTotal.indexOf(Math.max(...byHourTotal));

  // Dados adicionais
  const focus = focusMedio(sessions);
  const distrTipo = distribuicaoPorTipo(sessions, contents);
  const progMetas = progressaoPorMeta(sessions, goals);
  const { dominadas, vencidas } = cardsDominadas(contents);
  const insights = gerarInsights(sessions, byDow, ritmoSemanas);

  // Heatmap 90 dias
  const heatmap = buildHeatmap(sessions, 90);
  const maxMin = Math.max(1, ...heatmap.map((h) => h.minutes));

  return (
    <div>
      <div class="main-head">
        <div>
          <h1 class="main-title">Analytics</h1>
          <div class="main-sub">últimos 90 dias · dado local + Postgres</div>
        </div>
      </div>

      {sessions.length === 0 ? (
        <div class="panel">
          <div class="empty-state">
            <div class="ico">▰</div>
            <h3>Nenhum dado ainda</h3>
            <div>Faça sua primeira sessão de Pomodoro para começar a coletar dados.</div>
          </div>
        </div>
      ) : (
        <>
          {/* SEÇÃO 1: RITMO & VOLUME */}
          <div class="grid-4" style={{ marginBottom: '20px' }}>
            <div class="kpi">
              <div class="label">Ritmo atual</div>
              <div class="value">{ritmoAtual.toFixed(1)}h</div>
              <div class="sub">por semana</div>
            </div>
            <div class="kpi purple">
              <div class="label">Sessões este mês</div>
              <div class="value">{totalSessions}</div>
              <div class="sub">{totalHours.toFixed(0)}h total</div>
            </div>
            <div class="kpi cyan">
              <div class="label">Dias de prática</div>
              <div class="value">{byDow.filter((h) => h > 0).length}</div>
              <div class="sub">de 7 na semana</div>
            </div>
            <div class="kpi pink">
              <div class="label">Sessão média</div>
              <div class="value" style={{ fontSize: '22px' }}>
                {Math.round(avgSessionMin)}min
              </div>
              <div class="sub">tempo real</div>
            </div>
          </div>

          {/* SEÇÃO 2: QUALIDADE & APRENDIZADO */}
          <div class="grid-3" style={{ marginBottom: '20px' }}>
            <div class={'kpi ' + (cumprimento >= 70 ? 'ok' : cumprimento >= 40 ? 'warn' : 'err')}>
              <div class="label">Cumprimento</div>
              <div class="value">{cumprimento}%</div>
              <div class="sub">planejadas vs feitas</div>
            </div>
            {focus !== null && (
              <div class="kpi ok">
                <div class="label">Energia</div>
                <div class="value">{focus}</div>
                <div class="sub">focus médio</div>
              </div>
            )}
            {dominadas + vencidas > 0 && (
              <div class="kpi purple">
                <div class="label">Cards SM-2</div>
                <div class="value">{dominadas}</div>
                <div class="sub">dominadas ({vencidas} vencidas)</div>
              </div>
            )}
          </div>

          {/* INSIGHTS */}
          {insights.length > 0 && (
            <div
              class="panel"
              style={{
                marginBottom: '20px',
                background:
                  'linear-gradient(135deg, rgba(0,240,255,0.05) 0%, rgba(177,74,255,0.05) 100%)',
                borderLeft: '3px solid var(--cyan)',
              }}
            >
              <div class="panel-title">💡 INSIGHTS</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {insights.map((i, idx) => (
                  <div
                    key={idx}
                    style={{
                      fontFamily: 'var(--f-mono)',
                      fontSize: '12px',
                      color: 'var(--text)',
                      lineHeight: 1.5,
                    }}
                  >
                    {i}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* GRÁFICO: RITMO ÚLTIMAS 4 SEMANAS */}
          <div class="panel" style={{ marginBottom: '20px' }}>
            <div class="panel-title">📈 RITMO · ÚLTIMAS 4 SEMANAS</div>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(4, 1fr)',
                gap: '12px',
                height: '140px',
                alignItems: 'flex-end',
              }}
            >
              {ritmoSemanas.map((r) => {
                const maxRitmo = Math.max(1, ...ritmoSemanas.map((x) => x.hPerWeek));
                const pct = (r.hPerWeek / maxRitmo) * 100;
                return (
                  <div
                    key={r.week}
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'flex-end',
                      height: '100%',
                      gap: '6px',
                    }}
                  >
                    <div
                      style={{
                        fontFamily: 'var(--f-mono)',
                        fontSize: '11px',
                        color: 'var(--cyan)',
                        fontWeight: 600,
                      }}
                    >
                      {r.hPerWeek.toFixed(1)}h
                    </div>
                    <div
                      style={{
                        width: '100%',
                        height: `${Math.max(4, pct)}%`,
                        background: 'linear-gradient(180deg, var(--cyan), rgba(0,240,255,0.3))',
                        borderRadius: '4px 4px 0 0',
                        boxShadow: '0 0 8px rgba(0,240,255,0.4)',
                      }}
                    />
                    <div
                      style={{
                        fontFamily: 'var(--f-mono)',
                        fontSize: '10px',
                        color: 'var(--text-dim)',
                      }}
                    >
                      {r.label}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* PROGRESSÃO POR META */}
          {progMetas.length > 0 && (
            <div class="panel" style={{ marginBottom: '20px' }}>
              <div class="panel-title">🎯 PROGRESSÃO POR META</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {progMetas.map((m) => (
                  <div
                    key={m.goalId}
                    style={{ display: 'flex', gap: '12px', alignItems: 'center' }}
                  >
                    <div style={{ flex: 1 }}>
                      <div
                        style={{
                          fontSize: '12px',
                          color: 'var(--text)',
                          marginBottom: '4px',
                          fontWeight: 500,
                        }}
                      >
                        {m.title}
                      </div>
                      <div
                        style={{
                          width: '100%',
                          height: '6px',
                          background: 'rgba(0,8,24,0.6)',
                          borderRadius: '2px',
                          overflow: 'hidden',
                        }}
                      >
                        <div
                          style={{
                            width: `${m.pct}%`,
                            height: '100%',
                            background:
                              m.pct >= 90
                                ? 'var(--green)'
                                : m.pct >= 50
                                  ? 'var(--cyan)'
                                  : 'var(--amber)',
                            transition: 'width 0.3s ease',
                          }}
                        />
                      </div>
                    </div>
                    <div
                      style={{
                        fontFamily: 'var(--f-mono)',
                        fontSize: '11px',
                        color: 'var(--text-dim)',
                        minWidth: '60px',
                        textAlign: 'right',
                      }}
                    >
                      {m.horasDone.toFixed(0)}h / {m.horasTarget.toFixed(0)}h
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* DISTRIBUIÇÃO POR TIPO */}
          {distrTipo.length > 0 && (
            <div class="panel" style={{ marginBottom: '20px' }}>
              <div class="panel-title">📚 DISTRIBUIÇÃO POR TIPO</div>
              <div class="grid-2">
                {distrTipo.slice(0, 6).map((d) => (
                  <div
                    key={d.type}
                    style={{
                      padding: '12px',
                      background: 'rgba(0,8,24,0.4)',
                      borderRadius: 'var(--r-sm)',
                      border: '1px solid var(--border-soft)',
                    }}
                  >
                    <div
                      style={{
                        fontSize: '12px',
                        color: 'var(--text-dim)',
                        textTransform: 'capitalize',
                        marginBottom: '6px',
                      }}
                    >
                      {d.type}
                    </div>
                    <div
                      style={{
                        fontSize: '18px',
                        fontWeight: 700,
                        color: 'var(--cyan)',
                      }}
                    >
                      {d.horas.toFixed(1)}h
                    </div>
                    <div style={{ fontSize: '10px', color: 'var(--text-dim)' }}>
                      {d.sessoes} sessões
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* HEATMAP 90 DIAS */}
          <div class="grid-2" style={{ marginBottom: '20px' }}>
            <div class="panel">
              <div class="panel-title">
                ▰ <b>HEATMAP</b> · 90 DIAS
              </div>
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(15, 1fr)',
                  gap: '3px',
                }}
              >
                {heatmap.map((h) => {
                  const int = intensity(h.minutes, maxMin);
                  return (
                    <div
                      key={h.date.getTime()}
                      title={`${h.date.toLocaleDateString('pt-BR')}: ${Math.round(h.minutes)}min`}
                      style={{
                        aspectRatio: '1',
                        borderRadius: '3px',
                        background: INTENSITY_COLORS[int],
                        border: '1px solid var(--border-soft)',
                        cursor: 'default',
                      }}
                    />
                  );
                })}
              </div>
              <div
                style={{
                  display: 'flex',
                  gap: '8px',
                  marginTop: '14px',
                  alignItems: 'center',
                  fontFamily: 'var(--f-mono)',
                  fontSize: '10px',
                  color: 'var(--text-dim)',
                }}
              >
                menos
                {INTENSITY_COLORS.map((c, i) => (
                  <div
                    key={i}
                    style={{
                      width: '14px',
                      height: '14px',
                      background: c,
                      border: '1px solid var(--border-soft)',
                      borderRadius: '3px',
                    }}
                  />
                ))}
                mais
              </div>
            </div>

            <div class="panel">
              <div class="panel-title">
                ▰ <b>FOCO POR DIA DA SEMANA</b>
              </div>
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(7, 1fr)',
                  gap: '8px',
                  height: '160px',
                  alignItems: 'flex-end',
                }}
              >
                {byDow.map((h, dow) => {
                  const pct = (h / maxDow) * 100;
                  const isWeekend = dow === 0 || dow === 6;
                  return (
                    <div
                      key={dow}
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'flex-end',
                        height: '100%',
                        gap: '6px',
                      }}
                    >
                      <div
                        style={{
                          fontFamily: 'var(--f-mono)',
                          fontSize: '10px',
                          color: h > 0 ? 'var(--cyan)' : 'var(--text-dim)',
                        }}
                      >
                        {h.toFixed(1)}h
                      </div>
                      <div
                        style={{
                          width: '100%',
                          height: `${pct}%`,
                          minHeight: '2px',
                          background: isWeekend
                            ? 'linear-gradient(180deg, var(--purple), rgba(177,74,255,0.15))'
                            : 'linear-gradient(180deg, var(--cyan), rgba(0,240,255,0.15))',
                          borderRadius: '4px 4px 0 0',
                          boxShadow: isWeekend
                            ? '0 0 6px rgba(177,74,255,0.3)'
                            : '0 0 6px rgba(0,240,255,0.3)',
                        }}
                      />
                      <div
                        style={{
                          fontFamily: 'var(--f-mono)',
                          fontSize: '10px',
                          color: 'var(--text-dim)',
                          letterSpacing: '1px',
                        }}
                      >
                        {dowLabel(dow)}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* QUANDO VOCÊ ESTUDA */}
          <div class="panel" style={{ marginTop: '20px' }}>
            <div class="panel-title">
              ▰ <b>QUANDO VOCÊ ESTUDA</b>
              {byHourTotal[peakHour] > 0 && (
                <span
                  style={{ fontFamily: 'var(--f-mono)', fontSize: '10px', color: 'var(--cyan)' }}
                >
                  pico: {String(peakHour).padStart(2, '0')}h
                </span>
              )}
            </div>
            <div style={{ overflowX: 'auto' }}>
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: `34px repeat(${hourRange.length}, minmax(18px, 1fr))`,
                  gap: '3px',
                  minWidth: `${34 + hourRange.length * 21}px`,
                }}
              >
                {[0, 1, 2, 3, 4, 5, 6].flatMap((dow) => [
                  <div
                    key={`lbl${dow}`}
                    style={{
                      fontFamily: 'var(--f-mono)',
                      fontSize: '9px',
                      color: 'var(--text-dim)',
                      display: 'flex',
                      alignItems: 'center',
                    }}
                  >
                    {dowLabel(dow)}
                  </div>,
                  ...hourRange.map((h) => {
                    const min = hourGrid[dow][h];
                    const int = intensity(min, maxCell);
                    return (
                      <div
                        key={`${dow}-${h}`}
                        title={`${dowLabel(dow)} ${String(h).padStart(2, '0')}h: ${Math.round(min)}min`}
                        style={{
                          aspectRatio: '1',
                          borderRadius: '3px',
                          background: INTENSITY_COLORS[int],
                          border: '1px solid var(--border-soft)',
                        }}
                      />
                    );
                  }),
                ])}
                <div />
                {hourRange.map((h) => (
                  <div
                    key={`h${h}`}
                    style={{
                      fontFamily: 'var(--f-mono)',
                      fontSize: '8.5px',
                      color: 'var(--text-dim)',
                      textAlign: 'center',
                    }}
                  >
                    {h % 2 === 0 ? `${h}h` : ''}
                  </div>
                ))}
              </div>
            </div>
            <div
              style={{
                marginTop: '12px',
                fontFamily: 'var(--f-mono)',
                fontSize: '10px',
                color: 'var(--text-dim)',
                lineHeight: 1.6,
              }}
            >
              Baseado no horário real de início das sessões concluídas. Esse padrão alimenta o
              planner e o benchmark contra aprovados (F5).
            </div>
          </div>
        </>
      )}
    </div>
  );
}
